import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { Button } from '../../../../../components/ui/button';
import { Checkbox } from '../../../../../components/ui/checkbox';
import { Input } from '../../../../../components/ui/input';
import {
  mapPipeDataListRowsToPickerItems,
  type PipeDataListPickerItem,
} from '../../../api/pipeDataList';
import { pipeRowEligibleForRandomCompat } from '../../../helpers/mapPipeStatusTableRows';
import type { PipeStatusItem } from '../../../types';
import { Notification as customNotification } from '../../../../../components/ui/custom-notification';
import { buildSubmitPayload, estimateFirstReadRuns } from '../helpers/formatReport';
import { cn } from '../../../../../lib/utils';

export type PipePickMode = 'all' | 'batch' | 'few';
export type RunCountMode = 'auto' | 'min' | 'exact';
export type ShortlistMode = 'none' | 'deliver' | 'picks';

export interface SetupPipeDefaults {
  pipeId?: string;
  displayName?: string;
  pipeName?: string;
  /** Pipe group id from the selected pipe size (when grouped). */
  groupId?: string | null;
  hiloThreshold?: number;
  hilo_threshold?: number;
  hiloThresholdMm?: number;
  weldSeamExclusionDeg?: number;
  sixOclockExclusionDeg?: number;
  nominalOd?: number;
  nominalOdMax?: number;
  nominalOdMin?: number;
}

export interface SetupFormValues {
  pickMode: PipePickMode;
  pipeIds: string[];
  confidencePct: number;
  marginPct: number;
  runCountMode: RunCountMode;
  minOrders: number;
  exactOrders: string;
  shortlistMode: ShortlistMode;
  shortlistN: string;
  hiLoThreshold: string;
  weldSeamSeparationMm: string;
  sixOClockExclusionDeg: string;
  flippingAllowed: boolean | null;
  setAside: boolean | null;
  setAsideCapacity: string;
}

interface SetupViewProps {
  projectNumber: string;
  selectedPipe?: SetupPipeDefaults | null;
  isSubmitting?: boolean;
  onRun: (body: Record<string, unknown>) => void;
  /** Pipe Status table rows already loaded on Pipe Details — do not refetch. */
  pipeStatusRows?: PipeStatusItem[];
  pipeStatusLoading?: boolean;
}

const CONF_OPTS = [80, 90, 95, 99] as const;
const MIN_ORDERS_PRESETS = [1000, 2000, 5000] as const;
const EXACT_ORDERS_MIN = 2;
const EXACT_ORDERS_MAX = 5000;
const SHORTLIST_N_MIN = 2;

function optionalFiniteNumber(value: unknown): number | undefined {
  const n = Number(value);
  return Number.isFinite(n) ? n : undefined;
}

function formatDefaultNumber(value: number | undefined, digits = 2): string {
  if (value == null || !Number.isFinite(value)) return '';
  return String(Number(value.toFixed(digits)));
}

function nominalOdForSeamMm(pipe: SetupPipeDefaults | null | undefined): number | undefined {
  if (!pipe) return undefined;
  const od = Number(pipe.nominalOd ?? 0);
  if (od > 0) return od;
  const max = Number(pipe.nominalOdMax ?? 0);
  if (max > 0) return max;
  const min = Number(pipe.nominalOdMin ?? 0);
  if (min > 0) return min;
  return undefined;
}

/** Pipe setup stores seam as degrees; the API override expects surface distance (mm). */
function seamSeparationMmFromPipe(pipe: SetupPipeDefaults | null | undefined): number | undefined {
  const deg = optionalFiniteNumber(pipe?.weldSeamExclusionDeg);
  const od = nominalOdForSeamMm(pipe);
  if (deg == null || od == null) return undefined;
  return (deg / 360) * Math.PI * od;
}

function advancedDefaultsFromPipe(pipe: SetupPipeDefaults | null | undefined) {
  return {
    hiLoThreshold: formatDefaultNumber(
      optionalFiniteNumber(pipe?.hiloThreshold ?? pipe?.hilo_threshold ?? pipe?.hiloThresholdMm),
      2
    ),
    sixOClockExclusionDeg: formatDefaultNumber(optionalFiniteNumber(pipe?.sixOclockExclusionDeg), 1),
    weldSeamSeparationMm: formatDefaultNumber(seamSeparationMmFromPipe(pipe), 1),
  };
}


function SegButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'font-heading font-semibold text-[13.5px] px-3.5 py-2 border rounded-lg transition-colors',
        active
          ? 'border-hero-red text-blueprint-blue bg-[rgba(235,28,55,0.06)]'
          : 'border-[#E2E5EA] bg-white text-heritage-grey hover:border-heritage-grey'
      )}
    >
      {children}
    </button>
  );
}

export const SetupView: React.FC<SetupViewProps> = ({
  projectNumber,
  selectedPipe,
  isSubmitting,
  onRun,
  pipeStatusRows = [],
  pipeStatusLoading = false,
}) => {
  const [pickMode, setPickMode] = useState<PipePickMode>('all');
  const [confidencePct, setConfidencePct] = useState(95);
  const [marginPct, setMarginPct] = useState(5);
  const [runCountMode, setRunCountMode] = useState<RunCountMode>('auto');
  const [minOrders, setMinOrders] = useState<number>(2000);
  const [exactOrders, setExactOrders] = useState('5000');
  const [shortlistMode, setShortlistMode] = useState<ShortlistMode>('none');
  const [shortlistN, setShortlistN] = useState('200');
  const [hiLoThreshold, setHiLoThreshold] = useState('');
  const [weldSeamSeparationMm, setWeldSeamSeparationMm] = useState('');
  const [sixOClockExclusionDeg, setSixOClockExclusionDeg] = useState('');
  const [flippingAllowed, setFlippingAllowed] = useState<boolean | null>(false);
  const [setAside, setSetAside] = useState<boolean | null>(false);
  const [setAsideCapacity, setSetAsideCapacity] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const [pipeSearch, setPipeSearch] = useState('');
  const [selectedPipeIds, setSelectedPipeIds] = useState<Set<string>>(new Set());
  const pipeRows = pipeStatusRows;
  const pipeListLoading = pipeStatusLoading;

  // Prefill Advanced overrides from the selected pipe size whenever it changes.
  useEffect(() => {
    const defaults = advancedDefaultsFromPipe(selectedPipe);
    setHiLoThreshold(defaults.hiLoThreshold);
    setSixOClockExclusionDeg(defaults.sixOClockExclusionDeg);
    setWeldSeamSeparationMm(defaults.weldSeamSeparationMm);
  }, [selectedPipe?.pipeId]);

  const needsPicker = pickMode === 'batch' || pickMode === 'few';
  const estimatedRuns = estimateFirstReadRuns(confidencePct, marginPct);


  const bothEndsRows = useMemo(
    () =>
      pipeRows.filter((row) =>
        pipeRowEligibleForRandomCompat(row as unknown as Record<string, unknown>)
      ),
    [pipeRows]
  );

  const pipeList: PipeDataListPickerItem[] = useMemo(
    () => mapPipeDataListRowsToPickerItems(bothEndsRows),
    [bothEndsRows]
  );

  const filteredPipes = useMemo(() => {
    const q = pipeSearch.trim().toLowerCase();
    if (!q) return pipeList;
    return pipeList.filter((p) => p.pipeID.toLowerCase().includes(q));
  }, [pipeList, pipeSearch]);

  const totalPipeCount = pipeList.length;
  const selectedCount = selectedPipeIds.size;

  // Drop selections that are no longer in the both-ends list.
  useEffect(() => {
    const allowed = new Set(pipeList.map((p) => p.pipeID));
    setSelectedPipeIds((prev) => {
      const next = new Set(Array.from(prev).filter((id) => allowed.has(id)));
      if (next.size === prev.size) return prev;
      return next;
    });
  }, [pipeList]);

  const togglePipe = (pipeID: string) => {
    setSelectedPipeIds((prev) => {
      const next = new Set(prev);
      if (next.has(pipeID)) next.delete(pipeID);
      else next.add(pipeID);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    const ids = filteredPipes.map((p) => p.pipeID);
    setSelectedPipeIds((prev) => {
      const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) ids.forEach((id) => next.delete(id));
      else ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const exactOrdersValid = (() => {
    if (runCountMode !== 'exact') return true;
    const n = Number(exactOrders.trim());
    return Number.isFinite(n) && n >= EXACT_ORDERS_MIN && n <= EXACT_ORDERS_MAX;
  })();

  // Shortlist must be smaller than the lot (PRD); lot = all pipes or the selected subset.
  const shortlistLotSize =
    pickMode === 'all' ? totalPipeCount : selectedCount;
  const shortlistMax =
    shortlistLotSize > SHORTLIST_N_MIN ? shortlistLotSize - 1 : null;

  const shortlistNValid = (() => {
    if (shortlistMode === 'none') return true;
    const n = Number(shortlistN.trim());
    if (!Number.isFinite(n) || n < SHORTLIST_N_MIN) return false;
    if (shortlistMax != null && n > shortlistMax) return false;
    return true;
  })();

  const shortlistValidationMessage = (() => {
    if (shortlistMode === 'none' || shortlistNValid) return null;
    const n = Number(shortlistN.trim());
    if (shortlistMax != null && Number.isFinite(n) && n > shortlistMax) {
      return `Shortlist cannot be more than ${shortlistMax} pipes (must be below the ${shortlistLotSize}-pipe lot). You entered ${n}.`;
    }
    if (shortlistMax != null) {
      return `Enter count from ${SHORTLIST_N_MIN} to ${shortlistMax} (below the ${shortlistLotSize}-pipe lot).`;
    }
    return `Enter count of at least ${SHORTLIST_N_MIN}.`;
  })();

  // If the lot size loads/changes and N is now too high, clamp it.
  useEffect(() => {
    if (shortlistMode === 'none' || shortlistMax == null) return;
    const n = Number(shortlistN.trim());
    if (Number.isFinite(n) && n > shortlistMax) {
      setShortlistN(String(shortlistMax));
    }
  }, [shortlistMode, shortlistMax, shortlistN]);

  const handleRun = () => {
    if (!projectNumber) return;
    if (totalPipeCount === 0) return;
    if (needsPicker && selectedPipeIds.size === 0) return;

    if (!exactOrdersValid) {
      customNotification.warning(
        'Invalid build count',
        `Enter a whole number from ${EXACT_ORDERS_MIN} to ${EXACT_ORDERS_MAX.toLocaleString()}.`
      );
      return;
    }

    if (!shortlistNValid) {
      customNotification.warning(
        'Shortlist not allowed',
        shortlistValidationMessage ??
          'Enter a valid shortlist size below the pipe lot. The run was not started.'
      );
      return;
    }

    const parseOpt = (raw: string): number | null => {
      const t = raw.trim();
      if (!t) return null;
      const n = Number(t);
      return Number.isFinite(n) ? n : null;
    };

    const exactParsed = parseOpt(exactOrders);
    const shortlistParsed = parseOpt(shortlistN);
    const body = buildSubmitPayload({
      projectNumber,
      groupId:
        selectedPipe?.groupId != null && String(selectedPipe.groupId).trim() !== ''
          ? String(selectedPipe.groupId).trim()
          : null,
      pipeIds:
        pickMode === 'all' ? pipeList.map((p) => p.pipeID) : Array.from(selectedPipeIds),
      confidence: confidencePct / 100,
      margin: marginPct / 100,
      hiLoThreshold: parseOpt(hiLoThreshold),
      weldSeamSeparationMm: parseOpt(weldSeamSeparationMm),
      sixOClockExclusionDeg: parseOpt(sixOClockExclusionDeg),
      flippingAllowed,
      // No → 0; Yes + value → number; Yes + empty → omit (same as seam separation)
      setAsideCapacity: setAside === false ? 0 : setAside === true ? parseOpt(setAsideCapacity) : null,
      orders: runCountMode === 'exact' ? exactParsed : null,
      minOrders: runCountMode === 'min' ? minOrders : null,
      pipesNeeded: shortlistMode === 'picks' ? shortlistParsed : null,
      pipesToDeliver: shortlistMode === 'deliver' ? shortlistParsed : null,
    });
    onRun(body);
  };

  // Keep Run clickable when shortlist/exact are invalid so handleRun can show a top notification.
  const canRun =
    Boolean(projectNumber) &&
    !isSubmitting &&
    totalPipeCount > 0 &&
    (pickMode === 'all' || selectedPipeIds.size > 0);

  return (
  <div className="space-y-4 max-w-6xl mx-auto w-full">
      <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-hero-red" />
        <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-3">
          1 · Which pipes?
        </p>
        <div className="flex flex-wrap gap-2">
          <SegButton active={pickMode === 'all'} onClick={() => setPickMode('all')}>
            {totalPipeCount > 0
              ? `All ${totalPipeCount} pipe${totalPipeCount === 1 ? '' : 's'}`
              : 'All pipes'}
          </SegButton>
          <SegButton active={pickMode === 'batch'} onClick={() => setPickMode('batch')}>
            A batch
          </SegButton>
          <SegButton active={pickMode === 'few'} onClick={() => setPickMode('few')}>
            Pick a few
          </SegButton>
        </div>
        <p className="mt-2.5 text-[12.5px] text-heritage-grey">
          {pipeListLoading && pipeRows.length === 0
            ? 'Using the Pipe Details table — waiting for pipes to finish loading…'
            : pickMode === 'all'
            ? totalPipeCount > 0
              ? `All ${totalPipeCount} pipe${totalPipeCount === 1 ? '' : 's'} with both ends available (archived excluded).`
              : pipeRows.length === 0
                ? 'No pipes from the Pipe Details table yet. Open Pipe Details so the table can load.'
                : 'Only pipes with both ends available can be used (archived excluded).'
            : totalPipeCount > 0
              ? `${selectedCount} of ${totalPipeCount} selected. Only pipes with both ends available can be picked (archived excluded).`
              : pipeRows.length === 0
                ? 'No pipes from the Pipe Details table yet. Open Pipe Details so the table can load.'
                : 'Select pipe IDs with both ends available (archived excluded).'}
        </p>

        {needsPicker && (
          <div className="mt-4 space-y-3">
            {!selectedPipe?.pipeId ? (
              <div className="rounded-lg border border-[rgba(235,28,55,0.2)] bg-[rgba(235,28,55,0.06)] p-3 text-[13px] text-hero-red">
                Select a pipe size on the Pipefitter page to load pipes for picking.
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-heritage-grey" />
                  <Input
                    value={pipeSearch}
                    onChange={(e) => setPipeSearch(e.target.value)}
                    placeholder="Search pipe ID…"
                    className="pl-9"
                  />
                </div>
                <div className="flex items-center justify-between text-[12.5px] text-heritage-grey">
                  <button type="button" className="text-blueprint-blue font-semibold" onClick={toggleAllFiltered}>
                    {filteredPipes.every((p) => selectedPipeIds.has(p.pipeID)) && filteredPipes.length
                      ? 'Clear page'
                      : 'Select page'}
                  </button>
                  <span>{selectedPipeIds.size} selected</span>
                </div>
                <div className="max-h-56 overflow-auto border border-[#E2E5EA] rounded-lg">
                  {pipeListLoading ? (
                    <div className="flex items-center justify-center gap-2 py-8 text-heritage-grey text-[13px]">
                      <Loader2 className="h-4 w-4 animate-spin" /> Loading pipes…
                    </div>
                  ) : filteredPipes.length === 0 ? (
                    <div className="py-8 text-center text-[13px] text-heritage-grey">No pipes found</div>
                  ) : (
                    <ul className="divide-y divide-[#EEF0F3]">
                      {filteredPipes.map((p) => (
                        <li key={p.id} className="flex items-center gap-3 px-3 py-2">
                          <Checkbox
                            checked={selectedPipeIds.has(p.pipeID)}
                            onCheckedChange={() => togglePipe(p.pipeID)}
                          />
                          <span className="text-[13.5px] font-medium text-blueprint-blue">{p.pipeID}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <div className="mt-5 pt-4 border-t border-[#E2E5EA]">
          <label className="block text-[12.5px] font-semibold text-blueprint-blue mb-2">
            Need a shortlist from this lot? (optional)
          </label>
          <div className="flex flex-wrap gap-2">
            <SegButton
              active={shortlistMode === 'none'}
              onClick={() => setShortlistMode('none')}
            >
              No shortlist
            </SegButton>
            <SegButton
              active={shortlistMode === 'deliver'}
              onClick={() => setShortlistMode('deliver')}
            >
              Must deliver N
            </SegButton>
            <SegButton
              active={shortlistMode === 'picks'}
              onClick={() => setShortlistMode('picks')}
            >
              Best N picks
            </SegButton>
          </div>

          {shortlistMode !== 'none' && (
            <div className="mt-3">
              <Input
                value={shortlistN}
                onChange={(e) => {
                  const digits = e.target.value.replace(/[^\d]/g, '');
                  if (!digits) {
                    setShortlistN('');
                    return;
                  }
                  const n = Number(digits);
                  if (shortlistMax != null && Number.isFinite(n) && n > shortlistMax) {
                    setShortlistN(String(shortlistMax));
                    return;
                  }
                  setShortlistN(digits);
                }}
                inputMode="numeric"
                className={cn(
                  'max-w-[140px]',
                  !shortlistNValid && 'border-hero-red focus-visible:ring-hero-red'
                )}
                aria-invalid={!shortlistNValid}
                aria-label="Shortlist N"
              />
              {shortlistValidationMessage && (
                <p className="mt-1.5 text-[12.5px] text-hero-red">{shortlistValidationMessage}</p>
              )}
              {/* {shortlistNValid && shortlistMax != null && (
                <p className="mt-1.5 text-[12.5px] text-heritage-grey">
                  Max {shortlistMax} (must be below the {shortlistLotSize}-pipe lot).
                </p>
              )} */}
            </div>
          )}

          {/* <p className="mt-2.5 text-[12.5px] text-heritage-grey leading-relaxed">
            <span className="font-semibold text-blueprint-blue">Must deliver N</span> sends{' '}
            <code className="text-[12px]">pipesToDeliver</code>: the run sizes the shortlist itself
            until N pipes verify as welding in (what a stakeholder usually means).{' '}
            <span className="font-semibold text-blueprint-blue">Best N picks</span> sends{' '}
            <code className="text-[12px]">pipesNeeded</code>: exactly N picks, verified, even if
            fewer weld. One choice, one field: the pair is invalid and the UI cannot send it.
            Result renders on the Shortlist tab.
          </p> */}
        </div>
      </div>

      <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-hero-red" />
        <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-3">
          2 · How sure, and how tight
        </p>
        <label className="block text-[12.5px] font-semibold text-blueprint-blue mb-2">
          How sure — <span className="text-hero-red">{confidencePct}%</span>
        </label>
        <div className="flex flex-wrap gap-2 mb-4">
          {CONF_OPTS.map((c) => (
            <SegButton key={c} active={confidencePct === c} onClick={() => setConfidencePct(c)}>
              {c}%
            </SegButton>
          ))}
        </div>
        <label className="block text-[12.5px] font-semibold text-blueprint-blue mb-2">
          How tight — <span className="text-hero-red">±{marginPct}%</span>
        </label>
        <input
          type="range"
          min={1}
          max={10}
          step={0.5}
          value={marginPct}
          onChange={(e) => setMarginPct(Number(e.target.value))}
          className="w-full accent-hero-red"
        />
        <div className="mt-3.5 border-l-[3px] border-blueprint-blue bg-[rgba(0,39,63,0.05)] rounded-r-lg px-4 py-3 text-[13.5px] text-text-grey">
          <span className="font-semibold text-blueprint-blue">
            First read after about {estimatedRuns} runs.
          </span>{' '}
          Pinning the pipe-by-pipe watch list usually runs past this; the run decides the real total.
        </div>
      </div>

      <div className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-hero-red" />
        <p className="text-[11px] font-semibold tracking-wider uppercase text-heritage-grey mb-3">
          3 · How many builds?
        </p>
        <div className="flex flex-wrap gap-2">
          <SegButton active={runCountMode === 'auto'} onClick={() => setRunCountMode('auto')}>
            Automatic
          </SegButton>
          <SegButton active={runCountMode === 'min'} onClick={() => setRunCountMode('min')}>
            At least…
          </SegButton>
          <SegButton active={runCountMode === 'exact'} onClick={() => setRunCountMode('exact')}>
            Exactly…
          </SegButton>
        </div>

        {runCountMode === 'auto' && (
          <p className="mt-2.5 text-[12.5px] text-heritage-grey">
            Stops when the answer holds steady; the report explains the count. Sends neither field.{' '}
            <span className="font-semibold text-blueprint-blue">Recommended.</span>
          </p>
        )}

        {runCountMode === 'min' && (
          <div className="mt-3 space-y-2">
            <div className="flex flex-wrap gap-2">
              {MIN_ORDERS_PRESETS.map((n) => (
                <SegButton key={n} active={minOrders === n} onClick={() => setMinOrders(n)}>
                  {n.toLocaleString()}
                </SegButton>
              ))}
            </div>
            <p className="text-[12.5px] text-heritage-grey">
              Runs your number even if the answer settles earlier, and runs longer if needed. Sends{' '}
              <code className="text-[12px]">minOrders</code> (up to 5,000). For the stakeholder who
              wants a big number for assurance.
            </p>
          </div>
        )}

        {runCountMode === 'exact' && (
          <div className="mt-3 space-y-2">
            <Input
              value={exactOrders}
              onChange={(e) => setExactOrders(e.target.value.replace(/[^\d]/g, ''))}
              inputMode="numeric"
              className="max-w-[140px]"
              aria-label="Exact build count"
            />
            {!exactOrdersValid && (
              <p className="text-[12.5px] text-hero-red">Enter number from 2 to 5,000.</p>
            )}
            <p className="text-[12.5px] text-heritage-grey">
              Stops at this count, settled or not. Sends <code className="text-[12px]">orders</code>{' '}
              (2 to 5,000). If time runs out first, the report comes back cut short and says so
              honestly. For reproducing a previous run or a quick capped check.
            </p>
          </div>
        )}

        <p className="mt-2.5 text-[12.5px] text-heritage-grey">
          One choice, one field: the UI cannot send <code className="text-[12px]">orders</code> and{' '}
          <code className="text-[12px]">minOrders</code> together, which the API would reject.
        </p>
      </div>

      <details
        className="rounded-xl border border-[#E2E5EA] bg-white p-5 relative overflow-hidden group"
        open={advancedOpen}
        onToggle={(e) => setAdvancedOpen((e.target as HTMLDetailsElement).open)}
      >
        <summary className="list-none cursor-pointer font-heading font-semibold text-blueprint-blue [&::-webkit-details-marker]:hidden">
          Advanced · what-if overrides (optional) ▾
        </summary>
        <div className="mt-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[12.5px] font-semibold text-blueprint-blue mb-2">
                Hi-lo threshold (mm)
              </label>
              <Input
                value={hiLoThreshold}
                onChange={(e) => setHiLoThreshold(e.target.value)}
                placeholder="e.g. 1"
              />
            </div>
            <div>
              <label className="block text-[12.5px] font-semibold text-blueprint-blue mb-2">
                Seam separation (mm)
              </label>
              <Input
                value={weldSeamSeparationMm}
                onChange={(e) => setWeldSeamSeparationMm(e.target.value)}
                placeholder="e.g. 200"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[12.5px] font-semibold text-blueprint-blue mb-2">
                Six o&apos;clock exclusion (deg)
              </label>
              <Input
                value={sixOClockExclusionDeg}
                onChange={(e) => setSixOClockExclusionDeg(e.target.value)}
                placeholder="e.g. 90"
              />
            </div>
            <div>
              <label className="block text-[12.5px] font-semibold text-blueprint-blue mb-2">
                Turning end-for-end
              </label>
              <div className="flex flex-wrap gap-2">
                <SegButton active={flippingAllowed === true} onClick={() => setFlippingAllowed(true)}>
                  Allowed
                </SegButton>
                <SegButton active={flippingAllowed === false} onClick={() => setFlippingAllowed(false)}>
                  Off
                </SegButton>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
            <div>
              <label className="block text-[12.5px] font-semibold text-blueprint-blue mb-2">
                Set aside
              </label>
              <div className="flex flex-wrap gap-2">
                <SegButton active={setAside === true} onClick={() => setSetAside(true)}>
                  Yes
                </SegButton>
                <SegButton
                  active={setAside === false}
                  onClick={() => {
                    setSetAside(false);
                    setSetAsideCapacity('');
                  }}
                >
                  No
                </SegButton>
              </div>
            </div>
            {setAside === true ? (
              <div>
                <label className="block text-[12.5px] font-semibold text-blueprint-blue mb-2">
                  Capacity
                </label>
                <Input
                  value={setAsideCapacity}
                  onChange={(e) => setSetAsideCapacity(e.target.value.replace(/[^\d]/g, ''))}
                  inputMode="numeric"
                  placeholder="e.g. 10"
                  aria-label="Set aside capacity"
                />
              </div>
            ) : (
              <div className="hidden sm:block" aria-hidden />
            )}
          </div>
        </div>
        {/* <p className="mt-3.5 text-[12.5px] text-heritage-grey">
          Prefills from the selected pipe size. Clear a field to omit the override (API uses
          project default).
        </p> */}
      </details>

      <Button
        type="button"
        disabled={!canRun}
        onClick={handleRun}
        className="w-full h-12 font-heading font-bold text-[16px] bg-hero-red hover:bg-hero-red/90 text-white rounded-[10px]"
      >
        {isSubmitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Submitting…
          </>
        ) : (
          'Run report →'
        )}
      </Button>
    </div>
  );
};
